import React, { useMemo, useCallback } from 'react';
import { Show } from '../../types/Show';
import { useShowsData } from '../../hooks/data/useShowsData';
import HorizontalShowCarousel from '../ui/HorizontalShowCarousel';
import { LoadingSkeleton } from '../ui/LoadingStates';
import { ErrorMessage } from '../ui/ErrorStates';
import { useNavigate, Link } from 'react-router-dom';

interface RecentShowsWidgetContentProps {
  daysToShow?: number;
  showsPerDay?: number;
}

/**
 * Displays the most recent shows from today and yesterday in a single
 * horizontal carousel, ordered from newest to oldest.
 */
const RecentShowsWidgetContent: React.FC<RecentShowsWidgetContentProps> = ({
  daysToShow = 2,
  showsPerDay = 6,
}) => {
  const navigate = useNavigate();
  const { shows, loading, error } = useShowsData();

  const getShowStartDate = useCallback((show: Show) => {
    return new Date(show.date_start_time.replace('#', 'T'));
  }, []);

  const recentShows = useMemo(() => {
    if (!shows.length) {
      return [] as Show[];
    }

    const now = new Date();
    const startOfToday = new Date(now);
    startOfToday.setHours(0, 0, 0, 0);

    const startOfYesterday = new Date(startOfToday);
    startOfYesterday.setDate(startOfYesterday.getDate() - 1);

    const maxShows = Math.max(showsPerDay * daysToShow, showsPerDay * 2);

    return shows
      .filter((show) => {
        const start = getShowStartDate(show);
        return start >= startOfYesterday && start <= now;
      })
      .sort((a, b) => getShowStartDate(b).getTime() - getShowStartDate(a).getTime())
      .slice(0, maxShows);
  }, [shows, daysToShow, showsPerDay, getShowStartDate]);

  const handleShowClick = useCallback(
    (show: Show) => {
      navigate(`/show/${show.show_id}`);
    },
    [navigate]
  );

  if (loading) {
    return (
      <>
        <div className="mb-4 sm:mb-6">
          <LoadingSkeleton height="h-8" width="w-48" className="mb-2" />
        </div>
        <div className="flex gap-3 sm:gap-4 overflow-hidden">
          {Array.from({ length: Math.max(4, showsPerDay) }).map((_, index) => (
            <div
              key={index}
              className="flex-shrink-0 w-32 sm:w-[11.8rem] md:w-[11.8rem] space-y-2"
            >
              <LoadingSkeleton height="h-32" width="w-full" />
              <LoadingSkeleton height="h-4" width="w-full" />
              <LoadingSkeleton height="h-3" width="w-3/4" />
            </div>
          ))}
        </div>
      </>
    );
  }

  if (error) {
    return (
      <ErrorMessage
        type="network"
        title="Failed to Load Recent Shows"
        message={error}
        onRetry={() => window.location.reload()}
        showRetry={true}
        className="bg-gray-800/50 rounded-lg p-6"
      />
    );
  }

  if (recentShows.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-200 text-lg">No recent shows available</p>
      </div>
    );
  }

  return (
    <>
      <div className="mb-4 sm:mb-6 flex items-center justify-between">
        <h2 className="text-xl sm:text-2xl font-bold text-white">Recent Shows</h2>
        <Link
          to="/shows"
          className="text-red-400 hover:text-red-300 text-sm font-medium transition-colors flex items-center"
        >
          View All
          <svg
            className="ml-1 w-3 h-3"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
        </Link>
      </div>

      <HorizontalShowCarousel
        shows={recentShows}
        onShowClick={handleShowClick}
        testId="recent-shows-carousel"
        showsPerView={{ mobile: 2, tablet: 4, desktop: 6 }}
      />
    </>
  );
};

export default RecentShowsWidgetContent;
